import React, { useState, useEffect } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { useTheme } from '@material-ui/core/styles'
import Box from '@material-ui/core/Box'
import Container from '@material-ui/core/Container'
import Card from '@material-ui/core/Card'
import CardHeader from '@material-ui/core/CardHeader'
import CardContent from '@material-ui/core/CardContent'
import Collapse from '@material-ui/core/Collapse'
import Typography from '@material-ui/core/Typography'
import IconButton from '@material-ui/core/IconButton'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import ExpandLessIcon from '@material-ui/icons/ExpandLess'
import ReactBSAlert from 'react-bootstrap-sweetalert'
import FormGroup from '@material-ui/core/FormGroup'
import FormControl from '@material-ui/core/FormControl'
import Select from '@material-ui/core/Select'
import MenuItem from '@material-ui/core/MenuItem'
import Grid from '@material-ui/core/Grid'
import Loader from 'components/general/Loader'
import EnergyAPI from 'config/api/EnergyAPI'
import { onError } from 'config/lib/errorLib'
import EnergyDeliveriesTable from 'components/admin/Energy/EnergyDeliveriesTable'
import { getMoments } from 'config/helpers/Helpers'
import componentStyles from 'assets/theme/views/admin/alternative-dashboard.js'
import componentStylesCardDeck from 'assets/theme/components/cards/card-deck.js'
import moment from 'moment-timezone'
import { eeProductOptions } from 'config/constants/Constants'
import PeriodSelect from 'components/general/PeriodSelect'
import EnergyAnalyticsPanel from 'components/admin/Energy/EnergyAnalyticsPanel'
import PeopleIcon from '@material-ui/icons/People'
import CheckIcon from '@material-ui/icons/Check'
import CloseIcon from '@material-ui/icons/Close'
import ReportProblemIcon from '@material-ui/icons/ReportProblem'
import TrendingUpIcon from '@material-ui/icons/TrendingUp'
import AutorenewIcon from '@material-ui/icons/Autorenew'
import Chip from '@material-ui/core/Chip'
import Tooltip from '@material-ui/core/Tooltip'
import CardStats from 'components/argon/Cards/Dashboard/CardStats.js'

const useStyles = makeStyles(componentStyles)
const useStylesCardDeck = makeStyles(componentStylesCardDeck)

function EnergyDeliveries() {
    const classes = { ...useStyles(), ...useStylesCardDeck() }
    const theme = useTheme()
    const api = new EnergyAPI()

    const [showStatus, setShowStatus] = useState('active')
    const [companies, setCompanies] = useState([])
    const [allDeliveries, setAllDeliveries] = useState([])
    const [deliveries, setDeliveries] = useState([])
    const [showDeliveries, setShowDeliveries] = useState([])
    const [leads, setLeads] = useState([])
    const [period, setPeriod] = useState('thisWeek')
    const [fromDate, setFromDate] = useState('')
    const [toDate, setToDate] = useState('')
    const [product, setProduct] = useState('Solar')
    const [loading, setLoading] = useState(true)
    const [alert, setAlert] = useState(false)
    const [expectedRevenue, setExpectedRevenue] = useState(null)
    const [rerunningTargets, setRerunningTargets] = useState(false)
    const [analyticsRefreshKey, setAnalyticsRefreshKey] = useState(0)
    const [wasteData, setWasteData] = useState(null)
    const [hkCommissionOpen, setHkCommissionOpen] = useState(false)
    const [hkCommissionData, setHkCommissionData] = useState(null)
    const [hkCommissionLoading, setHkCommissionLoading] = useState(false)
    const [hkExpandedWeeks, setHkExpandedWeeks] = useState({})
    const [hkPaidWeeks, setHkPaidWeeks] = useState(new Set())
    const [hkTogglingWeek, setHkTogglingWeek] = useState(null)
    const [hkMonthFilter, setHkMonthFilter] = useState('all')

    useEffect(() => {
        loadData()
    }, [])

    async function loadData() {
        setLoading(true)
        try {
            const companies = await api.getCompanies()
            companies.sort((a, b) => a.name.localeCompare(b.name))

            const deliveries = await api.getEnergyDeliveries()
            console.log('energy deliveries: ', deliveries)
            const activeDeliveries = deliveries.filter((d) => d.active)

            let dates = getMoments('thisWeek')

            // Fetch leads for default product (Solar) from both sites
            let leads = []
            let eeLeads = await api.getEeLeadsPeriod('Solar', dates.fromDate.valueOf(), dates.toDate.valueOf())
            let sfLeads = await api.getSfLeadsPeriod('Solar', dates.fromDate.valueOf(), dates.toDate.valueOf())
            leads = leads.concat(eeLeads, sfLeads)
            leads.sort((a, b) => b.createdAt - a.createdAt)

            // Aggregate lead counts and revenue onto deliveries
            const enriched = enrichDeliveries(deliveries, leads)
            const solarDeliveries = enriched.filter(d => d.product === 'Solar')

            setCompanies(companies)
            // Keep allDeliveries as the RAW records — enrichDeliveries expands
            // source-override rows, so re-enriching must start from raw to avoid
            // double-expansion.
            setAllDeliveries(deliveries)
            setDeliveries(solarDeliveries)
            setShowDeliveries(solarDeliveries.filter(d => d.active))
            setLeads(leads)
            setFromDate(dates.fromDate)
            setToDate(dates.toDate)
            setLoading(false)
        } catch(e) {
            onError(e)
        }
    }

    function enrichDeliveries(deliveries, leads) {
        // Expand each delivery into display rows: a base row plus one row per
        // source override, so admins see per-bucket progress (e.g. a base EE row
        // and a separate SF row with its own price/cap/fill). Both rows reference
        // the same underlying record (__baseDelivery) and open the same modal.
        const ALL_SOURCES = ['ee', 'sf']
        let rows = []
        for (const d of deliveries) {
            const overrides = (d.sourceOverrides && typeof d.sourceOverrides === 'object') ? d.sourceOverrides : null
            const overriddenSources = overrides ? Object.keys(overrides) : []

            if (!overriddenSources.length) {
                rows.push({
                    ...d,
                    compositeKey: `${d.companyId}-${d.product}`,
                    __baseDelivery: d,
                    __sourceScope: null,
                    leadCount: 0,
                    revenue: 0
                })
                continue
            }

            // Base row: the sources not carved out by an override.
            const baseSources = ALL_SOURCES.filter(s => !overriddenSources.includes(s))
            const baseLabel = baseSources.map(s => s.toUpperCase()).join('/')
            rows.push({
                ...d,
                companyName: baseLabel ? `${d.companyName} (${baseLabel})` : d.companyName,
                compositeKey: `${d.companyId}-${d.product}-base`,
                __baseDelivery: d,
                __sourceScope: baseSources,
                leadCount: 0,
                revenue: 0
            })

            // One row per overridden source.
            for (const src of overriddenSources) {
                const ov = overrides[src] || {}
                rows.push({
                    ...d,
                    companyName: `${d.companyName} (${src.toUpperCase()})`,
                    compositeKey: `${d.companyId}-${d.product}-${src}`,
                    __baseDelivery: d,
                    __sourceScope: [src],
                    leadPrice: ov.leadPrice != null ? ov.leadPrice : d.leadPrice,
                    weeklyCap: ov.weeklyCap != null ? ov.weeklyCap : null,
                    minEELeads: null,
                    surgeEnabled: false,
                    leadCount: 0,
                    revenue: 0
                })
            }
        }

        for (let lead of leads) {
            // duplicate (re-allocated) leads are free and must not count towards totals/revenue
            if (lead.isDuplicate) {
                continue
            }
            if (lead.companyId || lead.isValid) {
                // Route the lead to the row for its company/product whose source
                // scope includes the lead's site (null scope = all sources).
                const index = rows.findIndex(r =>
                    r.companyId === lead.companyId &&
                    r.product === lead.product &&
                    (!r.__sourceScope || (lead.site && r.__sourceScope.includes(lead.site)))
                )
                if (index > -1) {
                    rows[index].leadCount += 1
                    rows[index].revenue += rows[index].leadPrice || 0
                }
            }
        }

        // Cap revenue for deliveries billed on cap basis
        for (let d of rows) {
            if (d.billingMode === 'cap' && d.weeklyCap != null) {
                const maxRevenue = d.weeklyCap * (d.leadPrice || 0)
                d.revenue = Math.min(d.revenue, maxRevenue)
            }
        }

        return rows
    }

    async function updatePeriod(period, fromDate=null, toDate=null) {
        setPeriod(period)
        setLoading(true)
        let dates = period === 'custom' ? {
            fromDate: moment(fromDate).startOf('day'),
            toDate: moment(toDate).endOf('day')
        } : getMoments(period)

        try {
            let leads = []
            let productList = product === 'all' ? eeProductOptions : [{ value: product }]
            for (let prod of productList) {
                let eeLeads = await api.getEeLeadsPeriod(prod.value, dates.fromDate.valueOf(), dates.toDate.valueOf())
                let sfLeads = await api.getSfLeadsPeriod(prod.value, dates.fromDate.valueOf(), dates.toDate.valueOf())
                leads = leads.concat(eeLeads, sfLeads)
            }
            leads.sort((a, b) => b.createdAt - a.createdAt)

            let baseDeliveries = product === 'all'
                ? allDeliveries.map(d => ({ ...d }))
                : allDeliveries.filter(d => d.product === product).map(d => ({ ...d }))

            const enriched = enrichDeliveries(baseDeliveries, leads)

            let checkStatus = showStatus === 'active'
            let filtered = enriched.filter(d => d.active === checkStatus)

            setDeliveries(enriched)
            setShowDeliveries(filtered)
            setLeads(leads)
            setFromDate(dates.fromDate)
            setToDate(dates.toDate)
            setLoading(false)
        } catch(e) {
            onError(e)
        }
    }

    async function updateProduct(newProduct) {
        setLoading(true)
        let dates = getMoments(period)
        try {
            let leads = []
            let productList = newProduct === 'all' ? eeProductOptions : [{ value: newProduct }]
            for (let prod of productList) {
                let eeLeads = await api.getEeLeadsPeriod(prod.value, dates.fromDate.valueOf(), dates.toDate.valueOf())
                let sfLeads = await api.getSfLeadsPeriod(prod.value, dates.fromDate.valueOf(), dates.toDate.valueOf())
                leads = leads.concat(eeLeads, sfLeads)
            }
            leads.sort((a, b) => b.createdAt - a.createdAt)

            let baseDeliveries = newProduct === 'all'
                ? allDeliveries.map(d => ({ ...d }))
                : allDeliveries.filter(d => d.product === newProduct).map(d => ({ ...d }))

            const enriched = enrichDeliveries(baseDeliveries, leads)

            let checkStatus = showStatus === 'active'
            let filtered = enriched.filter(d => d.active === checkStatus)

            setDeliveries(enriched)
            setShowDeliveries(filtered)
            setLeads(leads)
            setFromDate(dates.fromDate)
            setToDate(dates.toDate)
            setProduct(newProduct)
            setLoading(false)
        } catch(e) {
            onError(e)
        }
    }

    function updateDelivery(delivery) {
        // Update the underlying raw record, then re-expand/enrich the current
        // view (a record may render as multiple source-scoped rows).
        const newAll = allDeliveries.map(d =>
            (d.companyId === delivery.companyId && d.product === delivery.product)
                ? { ...d, ...delivery }
                : d
        )
        setAllDeliveries(newAll)

        const baseForView = product === 'all'
            ? newAll
            : newAll.filter(d => d.product === product)
        const enriched = enrichDeliveries(baseForView, leads)
        let checkStatus = showStatus === 'active'
        let filtered = enriched.filter(d => d.active === checkStatus)
        setDeliveries(enriched)
        setShowDeliveries(filtered)
    }

    function updateShowStatus(newStatus) {
        let checkStatus = newStatus === 'active'
        let filtered = deliveries.filter(d => d.active === checkStatus)
        setShowDeliveries(filtered)
        setShowStatus(newStatus)
    }

    function showSuccessAlert(text) {
        setAlert(
            <ReactBSAlert
                success
                style={{ display: 'block', marginTop: '-100px' }}
                title='Success'
                onConfirm={() => setAlert(false)}
                onCancel={() => setAlert(false)}
                confirmBtnBsStyle='success'
                confirmBtnText='Ok'
                btnSize=''
                confirmBtnStyle={{
                    marginRight: undefined,
                    borderColor: undefined,
                    boxShadow: undefined,
                }}
            >
                {text}
            </ReactBSAlert>
        )
    }

    async function rerunSolarTargets() {
        setRerunningTargets(true)
        try {
            await api.rerunEnergyTargets('Solar')
            await loadData()
            setAnalyticsRefreshKey((current) => current + 1)
            showSuccessAlert('Solar targets rerun successfully.')
        } catch (e) {
            onError(e)
        }
        setRerunningTargets(false)
    }

    async function loadHKCommission() {
        setHkCommissionLoading(true)
        try {
            const to = moment().format('YYYY-MM-DD')
            // Cover the full HK arrangement (started March 2026) so all weeks show
            const from = '2026-03-01'
            const result = await api.getHKCommissionSummary(from, to)
            setHkCommissionData(result)
            const paid = (result.weeks || []).filter(w => w.paid).map(w => w.weekEnding)
            setHkPaidWeeks(new Set(paid))
        } catch(e) {
            onError(e)
        }
        setHkCommissionLoading(false)
    }

    function toggleHkPanel() {
        if (!hkCommissionOpen && !hkCommissionData) {
            loadHKCommission()
        }
        setHkCommissionOpen(prev => !prev)
    }

    function toggleHkWeek(weekEnding) {
        setHkExpandedWeeks(prev => ({ ...prev, [weekEnding]: !prev[weekEnding] }))
    }

    async function toggleHkWeekPaid(week) {
        const newPaid = !hkPaidWeeks.has(week.weekEnding)
        setHkTogglingWeek(week.weekEnding)
        try {
            await api.setHKWeekPaid(week.weekEnding, newPaid)
            setHkPaidWeeks(prev => {
                const next = new Set(prev)
                if (newPaid) next.add(week.weekEnding)
                else next.delete(week.weekEnding)
                return next
            })
        } catch(e) {
            onError(e)
        }
        setHkTogglingWeek(null)
    }

    const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
    function monthKey(week) { return week.weekEnding.slice(0, 7) }
    function monthLabel(key) {
        const [y, m] = key.split('-')
        return `${MONTH_NAMES[parseInt(m, 10) - 1]} ${y}`
    }
    function avgNetForWeeks(weeksList) {
        const leads = weeksList.reduce((s, w) => s + w.totalHKLeads, 0)
        const net = weeksList.reduce((s, w) => s + (w.totalNetRevenue || 0), 0)
        return leads > 0 ? net / leads : 0
    }

    const [copiedWeek, setCopiedWeek] = useState(null)
    const [copiedBulk, setCopiedBulk] = useState(null)

    function formatWeekLine(week) {
        const d = week.weekEnding.slice(5).split('-').reverse().join('/')
        const parts = week.breakdown.filter(r => r.hkLeads > 0).map(r => `${r.companyName} (${r.hkLeads} × €${r.commissionPerLead})`)
        return `Week ending ${d}: ${parts.join(' + ')} = €${week.total.toFixed(2)}`
    }

    function copyWeekSummary(week, isEstimate) {
        const line = formatWeekLine(week) + (isEstimate ? ' (estimate)' : '')
        navigator.clipboard.writeText(line)
        setCopiedWeek(week.weekEnding)
        setTimeout(() => setCopiedWeek(null), 2000)
    }

    function copyBulkSummary(weeksToInclude, label) {
        const lines = weeksToInclude.map(formatWeekLine)
        const total = weeksToInclude.reduce((sum, w) => sum + w.total, 0)
        lines.push(`Total: €${total.toFixed(2)}`)
        lines.push(`Average net lead (after HK commission): €${avgNetForWeeks(weeksToInclude).toFixed(2)}`)
        navigator.clipboard.writeText(lines.join('\n'))
        setCopiedBulk(label)
        setTimeout(() => setCopiedBulk(null), 2000)
    }

    // Stats calculations
    const validLeads = leads.filter(lead => lead.companyId || lead.isValid)
    const rejectedLeads = leads.filter(lead => lead.companyId === null && !lead.isValid)
    const totalRevenue = deliveries.reduce((sum, d) => sum + (parseFloat(d.revenue) || 0), 0)
    const avgLeadValue = validLeads.length > 0 ? (totalRevenue / validLeads.length) : 0
    const unallocatedLeads = leads.filter(lead => lead.companyId === null && lead.isValid !== false)

    // Leads that re-entered via the SMS reactivation campaign this period.
    const reactivationLeads = leads.filter(lead => lead.isReactivation)

    // Group unallocated leads by county
    const unallocatedByCounty = {}
    for (let lead of unallocatedLeads) {
        const county = lead.county || 'Unknown'
        unallocatedByCounty[county] = (unallocatedByCounty[county] || 0) + 1
    }
    const soleCoverageCounties = (wasteData && wasteData.soleCoverageCounties) || []
    const sortedUnallocatedCounties = Object.entries(unallocatedByCounty)
        .sort((a, b) => b[1] - a[1])

    // Group unallocated leads by product (for breakdown when 'all' selected)
    const unallocatedByProduct = {}
    for (let lead of unallocatedLeads) {
        const prod = lead.product || 'Unknown'
        unallocatedByProduct[prod] = (unallocatedByProduct[prod] || 0) + 1
    }
    const sortedUnallocatedProducts = Object.entries(unallocatedByProduct)
        .sort((a, b) => b[1] - a[1])

    return (
        <>
            {alert}
            <Container
                maxWidth={false}
                component={Box}
                marginTop='2rem'
                classes={{ root: classes.containerRoot }}
            >
                {!loading ? (
                    <Grid container>
                        <Grid item xs={12}>
                            <Grid
                                container
                                component={Box}
                                alignItems='center'
                                justifyContent='flex-start'
                                style={{marginBottom: '2rem'}}
                                direction='row'
                            >
                                <Grid item xs={12} md={2}>
                                    <PeriodSelect
                                        period={period}
                                        updatePeriod={(period, fromDate, toDate) => updatePeriod(period, fromDate, toDate)}
                                        fromDate={fromDate}
                                        toDate={toDate}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <FormGroup style={{marginBottom: '0'}}>
                                        <FormControl variant='outlined'>
                                            <Select
                                                multiple={false}
                                                value={product}
                                                onChange={(e) => updateProduct(e.target.value)}
                                            >
                                                <MenuItem value={'all'}>All Products</MenuItem>
                                                {eeProductOptions.map((item) => (
                                                    <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </FormGroup>
                                    <Box
                                        fontSize='12px'
                                        color={theme.palette.gray[600]}
                                        marginTop='0.5rem'
                                        textAlign='left'
                                    >
                                        <Box component='small' fontSize='100%'>
                                            Product
                                        </Box>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <FormGroup style={{marginBottom: '0'}}>
                                        <FormControl variant='outlined' fullWidth>
                                            <Select
                                                multiple={false}
                                                value={showStatus}
                                                onChange={(e) => updateShowStatus(e.target.value)}
                                            >
                                                <MenuItem value='active'>Active</MenuItem>
                                                <MenuItem value='inactive'>Inactive</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </FormGroup>
                                    <Box
                                        fontSize='12px'
                                        color={theme.palette.gray[600]}
                                        marginTop='0.5rem'
                                        textAlign='left'
                                    >
                                        <Box component='small' fontSize='100%'>
                                            Status
                                        </Box>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Grid>

                        {/* Stats Cards */}
                        <Grid item xs={12}>
                            <Grid container direction='row' spacing={1} style={{marginBottom: '2rem', display: 'flex', flexWrap: 'wrap'}}>
                                <Grid item xs={12} sm={6} style={{flex: '1 1 0', minWidth: 180}}>
                                    <CardStats
                                        subtitle='Revenue'
                                        title={'€' + totalRevenue.toFixed(2)}
                                        icon={CheckIcon}
                                        color='bgSuccess'
                                        footer={expectedRevenue > 0 ? (
                                            <Tooltip title='Best-case revenue if every buyer hits their full weekly cap' arrow>
                                                <Box component='span' fontSize='12px' color='#8898aa' style={{ cursor: 'help' }}>
                                                    Potential: <strong>€{expectedRevenue.toLocaleString('en-IE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                                                </Box>
                                            </Tooltip>
                                        ) : null}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6} style={{flex: '1 1 0', minWidth: 180}}>
                                    <CardStats
                                        subtitle='Valid Leads'
                                        title={String(validLeads.length)}
                                        icon={PeopleIcon}
                                        color='bgPrimary'
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6} style={{flex: '1 1 0', minWidth: 180}}>
                                    <Tooltip
                                        title='Total revenue from allocated leads divided by the number of valid leads in the selected period'
                                        arrow
                                        placement='top'
                                    >
                                        <div>
                                            <CardStats
                                                subtitle='Avg Lead Value'
                                                title={'€' + avgLeadValue.toFixed(2)}
                                                icon={TrendingUpIcon}
                                                color='bgInfo'
                                            />
                                        </div>
                                    </Tooltip>
                                </Grid>
                                <Grid item xs={12} sm={6} style={{flex: '1 1 0', minWidth: 180}}>
                                    <CardStats
                                        subtitle='Unallocated'
                                        title={String(unallocatedLeads.length)}
                                        icon={ReportProblemIcon}
                                        color={unallocatedLeads.length > 0 ? 'bgWarning' : 'bgInfo'}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6} style={{flex: '1 1 0', minWidth: 180}}>
                                    <CardStats
                                        subtitle='Rejected'
                                        title={String(rejectedLeads.length)}
                                        icon={CloseIcon}
                                        color='bgError'
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6} style={{flex: '1 1 0', minWidth: 180}}>
                                    <Tooltip
                                        title='Leads that re-entered via the SMS reactivation campaign in the selected period'
                                        arrow
                                        placement='top'
                                    >
                                        <div>
                                            <CardStats
                                                subtitle='SMS Reactivation'
                                                title={String(reactivationLeads.length)}
                                                icon={AutorenewIcon}
                                                color='bgInfo'
                                            />
                                        </div>
                                    </Tooltip>
                                </Grid>
                            </Grid>
                        </Grid>

                        {/* Unallocated County Breakdown */}
                        {unallocatedLeads.length > 0 && (
                            <Grid item xs={12}>
                                <Box
                                    style={{
                                        backgroundColor: '#fff',
                                        borderRadius: '6px',
                                        border: '1px solid #fb6340',
                                        padding: '1rem 1.5rem',
                                        marginBottom: '1.5rem'
                                    }}
                                >
                                    <Box
                                        style={{
                                            fontWeight: 600,
                                            fontSize: '14px',
                                            color: '#525f7f',
                                            marginBottom: '0.75rem'
                                        }}
                                    >
                                        ⚠️ Unallocated Leads — {unallocatedLeads.length} lead{unallocatedLeads.length !== 1 ? 's' : ''} with no buyer
                                    </Box>
                                    <Box display='flex' flexWrap='wrap' style={{ gap: '8px' }}>
                                        {product === 'all' ? (
                                            sortedUnallocatedProducts.map(([prod, count]) => (
                                                <Chip
                                                    key={prod}
                                                    label={`${prod} (${count})`}
                                                    style={{
                                                        backgroundColor: '#5e72e4',
                                                        color: '#fff',
                                                        fontWeight: 600,
                                                        fontSize: '13px'
                                                    }}
                                                />
                                            ))
                                        ) : (
                                            sortedUnallocatedCounties.map(([county, count]) => {
                                                const isSole = soleCoverageCounties.includes(county)
                                                return (
                                                    <Chip
                                                        key={county}
                                                        label={`${isSole ? '⚠️ ' : ''}${county} (${count})`}
                                                        style={{
                                                            backgroundColor: isSole ? '#ffc107' : '#fb6340',
                                                            color: isSole ? '#856404' : '#fff',
                                                            fontWeight: 600,
                                                            fontSize: '13px',
                                                            border: isSole ? '2px solid #ff9800' : 'none'
                                                        }}
                                                    />
                                                )
                                            })
                                        )}
                                    </Box>
                                </Box>
                            </Grid>
                        )}

                        {/* Deliveries Table */}
                        <Grid item xs={12}>
                            <EnergyDeliveriesTable
                                product={product}
                                deliveries={(() => {
                                    let items = showDeliveries.map(item => ({
                                        ...item,
                                        // Preserve the per-source unique key from enrichDeliveries
                                        // (source-override rows share companyId+product).
                                        compositeKey: item.compositeKey || `${item.companyId}-${item.product}`
                                    }))
                                    if (product === 'all') {
                                        // Build lead count per product for sorting
                                        const productLeadCounts = {}
                                        for (let d of items) {
                                            productLeadCounts[d.product] = (productLeadCounts[d.product] || 0) + (d.leadCount || 0)
                                        }
                                        items.sort((a, b) => {
                                            // Primary: product group by total leads desc
                                            const productDiff = (productLeadCounts[b.product] || 0) - (productLeadCounts[a.product] || 0)
                                            if (productDiff !== 0) return productDiff
                                            // Secondary: within same product, largest weekly cap first (null = unlimited = top)
                                            const capA = a.weeklyCap == null ? Infinity : a.weeklyCap
                                            const capB = b.weeklyCap == null ? Infinity : b.weeklyCap
                                            return capB - capA
                                        })
                                    } else {
                                        // Single product: sort by largest weekly cap first
                                        items.sort((a, b) => {
                                            const capA = a.weeklyCap == null ? Infinity : a.weeklyCap
                                            const capB = b.weeklyCap == null ? Infinity : b.weeklyCap
                                            return capB - capA
                                        })
                                    }
                                    return items
                                })()}
                                companies={companies}
                                refreshDeliveries={() => loadData()}
                                updateDelivery={(delivery) => updateDelivery(delivery)}
                                showSuccessAlert={(text) => showSuccessAlert(text)}
                                title={showStatus === 'active' ? 'Energy Deliveries' : 'Inactive Energy Deliveries'}
                                period={period}
                                showRerunTargetsButton={product === 'Solar'}
                                onRerunTargets={() => rerunSolarTargets()}
                                rerunningTargets={rerunningTargets}
                            />
                        </Grid>

                        {/* Analytics Panel */}
                        <Grid item xs={12}>
                            <EnergyAnalyticsPanel product={product} onExpectedRevenue={setExpectedRevenue} onWasteData={setWasteData} refreshKey={analyticsRefreshKey} liveUnallocated={unallocatedLeads.length} />
                        </Grid>

                        {/* HK Commission Panel */}
                        <Grid item xs={12}>
                            <Card style={{ marginBottom: '1.5rem' }}>
                                <CardHeader
                                    title={
                                        <Box display='flex' alignItems='center' justifyContent='space-between' style={{ cursor: 'pointer' }}
                                            onClick={toggleHkPanel}>
                                            <Box>
                                                <Typography variant='h6' style={{ fontWeight: 700, fontSize: '14px', textTransform: 'uppercase', color: '#32325d' }}>
                                                    HK Commission
                                                </Typography>
                                                <Typography variant='caption' style={{ color: '#adb5bd' }}>
                                                    Weekly HK lead commission &amp; payments
                                                </Typography>
                                            </Box>
                                            <IconButton size='small'>
                                                {hkCommissionOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                            </IconButton>
                                        </Box>
                                    }
                                />
                                <Collapse in={hkCommissionOpen}>
                                    <CardContent style={{ padding: '24px 24px 20px' }}>
                                        {hkCommissionLoading ? (
                                            <Box display='flex' justifyContent='center' padding='1rem'>
                                                <Loader />
                                            </Box>
                                        ) : hkCommissionData ? (
                                            <>
                                                {/* Current week estimate */}
                                                {hkCommissionData.currentWeek && hkCommissionData.currentWeek.totalHKLeads > 0 && (
                                                    <Box marginBottom='1.5rem'>
                                                        <Box fontWeight='600' fontSize='13px' color='#8898aa' marginBottom='0.5rem' style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                                            Current Week (Estimate)
                                                        </Box>
                                                        <Box
                                                            style={{
                                                                border: '1px dashed #5e72e4',
                                                                borderRadius: '6px',
                                                                padding: '0.75rem 1rem',
                                                                backgroundColor: '#f8f9fe'
                                                            }}
                                                        >
                                                            <Box display='flex' justifyContent='space-between' alignItems='center'>
                                                                <Box>
                                                                    <Box fontWeight='600' fontSize='14px' color='#525f7f'>
                                                                        Week ending {hkCommissionData.currentWeek.weekEnding}
                                                                    </Box>
                                                                    <Box fontSize='12px' color='#8898aa' marginTop='0.25rem'>
                                                                        {hkCommissionData.currentWeek.totalHKLeads} HK leads ≈ €{hkCommissionData.currentWeek.blendedPricePerLead}/lead avg commission (based on weekly caps) · net €{hkCommissionData.currentWeek.avgNetPerLead}/lead
                                                                    </Box>
                                                                </Box>
                                                                <Box display='flex' alignItems='center' style={{ gap: '1rem' }}>
                                                                    <Box
                                                                        component='span'
                                                                        onClick={() => copyWeekSummary(hkCommissionData.currentWeek, true)}
                                                                        style={{ fontSize: '12px', color: copiedWeek === hkCommissionData.currentWeek.weekEnding ? '#2dce89' : '#5e72e4', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                                                                    >
                                                                        {copiedWeek === hkCommissionData.currentWeek.weekEnding ? '✓ Copied' : '⎘ Copy'}
                                                                    </Box>
                                                                    <Box fontWeight='700' fontSize='16px' color='#5e72e4'>
                                                                        €{hkCommissionData.currentWeek.total.toFixed(2)}
                                                                    </Box>
                                                                </Box>
                                                            </Box>
                                                            <Box marginTop='0.5rem'>
                                                                {hkCommissionData.currentWeek.breakdown.filter(r => r.hkLeads > 0).map((row, i) => (
                                                                    <Box key={i} fontSize='12px' color='#8898aa' marginTop='0.2rem'>
                                                                        {row.companyName} — {row.product}: {row.billed} cap − {row.preHkLeads} pre-HK = <strong>{row.hkLeads} HK leads</strong> @ €{row.commissionPerLead} comm{row.reduction > 0 ? ` (−€${row.reduction} split, sold €${row.salePrice})` : ''} → net €{row.netPerLead}
                                                                    </Box>
                                                                ))}
                                                            </Box>
                                                        </Box>
                                                    </Box>
                                                )}

                                                {/* Historical weeks */}
                                                {hkCommissionData.weeks && hkCommissionData.weeks.length > 0 ? (
                                                    (() => {
                                                        const allWeeks = hkCommissionData.weeks
                                                        const months = [...new Set(allWeeks.map(monthKey))]
                                                        const filteredWeeks = hkMonthFilter === 'all' ? allWeeks : allWeeks.filter(w => monthKey(w) === hkMonthFilter)
                                                        const unpaidWeeks = filteredWeeks.filter(w => !hkPaidWeeks.has(w.weekEnding))
                                                        const totalOwed = unpaidWeeks.reduce((s, w) => s + w.total, 0)
                                                        const periodAvgNet = avgNetForWeeks(filteredWeeks)
                                                        const groups = []
                                                        filteredWeeks.forEach(w => {
                                                            const mk = monthKey(w)
                                                            let g = groups.find(x => x.month === mk)
                                                            if (!g) { g = { month: mk, weeks: [] }; groups.push(g) }
                                                            g.weeks.push(w)
                                                        })
                                                        return (
                                                    <>
                                                        <Box display='flex' alignItems='center' justifyContent='space-between' marginBottom='0.5rem' flexWrap='wrap' style={{ gap: '0.5rem' }}>
                                                            <Box display='flex' alignItems='center' style={{ gap: '0.75rem' }}>
                                                                <Box fontWeight='600' fontSize='13px' color='#8898aa' style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                                                    Completed Weeks
                                                                </Box>
                                                                <select
                                                                    value={hkMonthFilter}
                                                                    onChange={(e) => setHkMonthFilter(e.target.value)}
                                                                    style={{ fontSize: '12px', color: '#525f7f', border: '1px solid #e9ecef', borderRadius: '4px', padding: '0.2rem 0.4rem', background: '#fff' }}
                                                                >
                                                                    <option value='all'>All months</option>
                                                                    {months.map(m => (
                                                                        <option key={m} value={m}>{monthLabel(m)}</option>
                                                                    ))}
                                                                </select>
                                                            </Box>
                                                            <Box display='flex' style={{ gap: '0.5rem' }}>
                                                                {unpaidWeeks.length > 0 && (
                                                                    <span
                                                                        onClick={() => copyBulkSummary(unpaidWeeks, 'unpaid')}
                                                                        style={{ fontSize: '11px', color: copiedBulk === 'unpaid' ? '#2dce89' : '#8898aa', cursor: 'pointer', userSelect: 'none' }}
                                                                    >
                                                                        {copiedBulk === 'unpaid' ? '✓ Copied' : '⎘ Copy Unpaid'}
                                                                    </span>
                                                                )}
                                                                <span
                                                                    onClick={() => copyBulkSummary(filteredWeeks, 'all')}
                                                                    style={{ fontSize: '11px', color: copiedBulk === 'all' ? '#2dce89' : '#8898aa', cursor: 'pointer', userSelect: 'none' }}
                                                                >
                                                                    {copiedBulk === 'all' ? '✓ Copied' : '⎘ Copy All'}
                                                                </span>
                                                            </Box>
                                                        </Box>
                                                        <Box style={{ overflowX: 'auto' }}>
                                                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                                                                <thead>
                                                                    <tr style={{ borderBottom: '2px solid #e9ecef' }}>
                                                                        <th style={{ textAlign: 'left', padding: '0.5rem 0.75rem', color: '#8898aa', fontWeight: 600 }}>Week Ending</th>
                                                                        <th style={{ textAlign: 'right', padding: '0.5rem 0.75rem', color: '#8898aa', fontWeight: 600 }}>HK Leads</th>
                                                                        <th style={{ textAlign: 'right', padding: '0.5rem 0.75rem', color: '#8898aa', fontWeight: 600 }}>Comm/Lead</th>
                                                                        <th style={{ textAlign: 'right', padding: '0.5rem 0.75rem', color: '#8898aa', fontWeight: 600 }}>Net/Lead</th>
                                                                        <th style={{ textAlign: 'right', padding: '0.5rem 0.75rem', color: '#8898aa', fontWeight: 600 }}>Total</th>
                                                                        <th style={{ textAlign: 'center', padding: '0.5rem 0.75rem', color: '#8898aa', fontWeight: 600 }}></th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {groups.map((g) => {
                                                                        const gLeads = g.weeks.reduce((s, w) => s + w.totalHKLeads, 0)
                                                                        const gTotal = g.weeks.reduce((s, w) => s + w.total, 0)
                                                                        const gUnpaid = g.weeks.filter(w => !hkPaidWeeks.has(w.weekEnding)).reduce((s, w) => s + w.total, 0)
                                                                        const gComm = gLeads > 0 ? (gTotal / gLeads) : 0
                                                                        const gNet = avgNetForWeeks(g.weeks)
                                                                        return (
                                                                            <React.Fragment key={g.month}>
                                                                                {g.weeks.map((week) => {
                                                                                    const isPaid = hkPaidWeeks.has(week.weekEnding)
                                                                                    return (
                                                                                        <React.Fragment key={week.weekEnding}>
                                                                                            <tr
                                                                                                style={{ borderBottom: hkExpandedWeeks[week.weekEnding] ? 'none' : '1px solid #e9ecef', cursor: 'pointer' }}
                                                                                                onClick={() => toggleHkWeek(week.weekEnding)}
                                                                                            >
                                                                                                <td style={{ padding: '0.6rem 0.75rem', color: '#525f7f', fontWeight: 500 }}>
                                                                                                    {week.weekEnding}
                                                                                                    {isPaid && <span style={{ marginLeft: '0.4rem', fontSize: '10px', color: '#2dce89', fontWeight: 700 }}>PAID</span>}
                                                                                                </td>
                                                                                                <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right', color: '#525f7f' }}>{week.totalHKLeads}</td>
                                                                                                <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right', color: '#525f7f' }}>€{week.blendedPricePerLead}</td>
                                                                                                <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right', color: '#525f7f' }}>€{week.avgNetPerLead}</td>
                                                                                                <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right', fontWeight: 700, color: isPaid ? '#2dce89' : '#f5365c' }}>€{week.total.toFixed(2)}</td>
                                                                                                <td style={{ padding: '0.6rem 0.75rem', textAlign: 'center', whiteSpace: 'nowrap' }}>
                                                                                                    <span
                                                                                                        onClick={(e) => { e.stopPropagation(); toggleHkWeekPaid(week) }}
                                                                                                        style={{ fontSize: '11px', color: isPaid ? '#2dce89' : '#8898aa', cursor: 'pointer', userSelect: 'none' }}
                                                                                                    >
                                                                                                        {hkTogglingWeek === week.weekEnding ? '…' : (isPaid ? '✓ Paid' : 'Mark paid')}
                                                                                                    </span>
                                                                                                    <span
                                                                                                        onClick={(e) => { e.stopPropagation(); copyWeekSummary(week, false) }}
                                                                                                        style={{ fontSize: '11px', color: copiedWeek === week.weekEnding ? '#2dce89' : '#8898aa', cursor: 'pointer', userSelect: 'none', marginLeft: '0.5rem' }}
                                                                                                    >
                                                                                                        {copiedWeek === week.weekEnding ? '✓ Copied' : '⎘ Copy'}
                                                                                                    </span>
                                                                                                    <span style={{ fontSize: '11px', color: '#8898aa', marginLeft: '0.5rem' }}>
                                                                                                        {hkExpandedWeeks[week.weekEnding] ? '▲' : '▼'}
                                                                                                    </span>
                                                                                                </td>
                                                                                            </tr>
                                                                                            {hkExpandedWeeks[week.weekEnding] && (
                                                                                                <tr style={{ borderBottom: '1px solid #e9ecef', backgroundColor: '#f6f9fc' }}>
                                                                                                    <td colSpan={6} style={{ padding: '0.5rem 1.5rem 0.75rem' }}>
                                                                                                        {week.breakdown.filter(r => r.hkLeads > 0).map((row, i) => (
                                                                                                            <Box key={i} fontSize='12px' color='#8898aa' marginTop='0.25rem'>
                                                                                                                {row.companyName} — {row.product}: {row.billed} billed − {row.preHkLeads} pre-HK = <strong>{row.hkLeads} HK leads</strong> @ €{row.commissionPerLead} comm{row.reduction > 0 ? ` (−€${row.reduction} split, sold €${row.salePrice})` : ''} → net €{row.netPerLead}
                                                                                                            </Box>
                                                                                                        ))}
                                                                                                    </td>
                                                                                                </tr>
                                                                                            )}
                                                                                        </React.Fragment>
                                                                                    )
                                                                                })}
                                                                                <tr style={{ borderBottom: '1px solid #e9ecef', backgroundColor: '#f6f9fc' }}>
                                                                                    <td style={{ padding: '0.5rem 0.75rem', fontWeight: 700, color: '#525f7f' }}>{monthLabel(g.month)}</td>
                                                                                    <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right', fontWeight: 600, color: '#525f7f' }}>{gLeads}</td>
                                                                                    <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right', color: '#8898aa' }}>€{gComm.toFixed(2)}</td>
                                                                                    <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right', color: '#8898aa' }}>€{gNet.toFixed(2)}</td>
                                                                                    <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right', fontWeight: 700, color: '#525f7f' }}>
                                                                                        €{gTotal.toFixed(2)}
                                                                                        {gUnpaid > 0 && gUnpaid !== gTotal && <Box fontSize='10px' color='#f5365c' fontWeight={600}>€{gUnpaid.toFixed(2)} unpaid</Box>}
                                                                                    </td>
                                                                                    <td></td>
                                                                                </tr>
                                                                            </React.Fragment>
                                                                        )
                                                                    })}
                                                                    <tr style={{ borderTop: '2px solid #e9ecef', backgroundColor: '#f6f9fc' }}>
                                                                        <td colSpan={3} style={{ padding: '0.6rem 0.75rem', fontWeight: 700, color: '#525f7f' }}>
                                                                            Total Owed
                                                                            <Box fontSize='11px' color='#8898aa' fontWeight={500} marginTop='0.15rem'>Avg net/lead: €{periodAvgNet.toFixed(2)}</Box>
                                                                        </td>
                                                                        <td></td>
                                                                        <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right', fontWeight: 700, color: '#f5365c', fontSize: '15px' }}>
                                                                            €{totalOwed.toFixed(2)}
                                                                        </td>
                                                                        <td></td>
                                                                    </tr>
                                                                </tbody>
                                                            </table>
                                                        </Box>
                                                    </>
                                                        )
                                                    })()
                                                ) : (
                                                    <Box fontSize='13px' color='#8898aa'>
                                                        No completed weeks with HK commission found.
                                                    </Box>
                                                )}
                                            </>
                                        ) : (
                                            <Box fontSize='13px' color='#8898aa'>Failed to load HK commission data.</Box>
                                        )}
                                    </CardContent>
                                </Collapse>
                            </Card>
                        </Grid>
                    </Grid>
                ) : (
                    <Grid container justifyContent='flex-start'>
                        <Grid item xs={12}>
                            <Box display='flex' justifyContent='center' alignItems='center' style={{height: '20vh'}}>
                                <Loader />
                            </Box>
                        </Grid>
                    </Grid>
                )}
            </Container>
        </>
    )
}

export default EnergyDeliveries
